if (!window.StockCubeCore) window.StockCubeCore = {};

window.StockCubeCore.calculateLabelPosition = function (
    barInfo, rotationData = null, targetX = null, targetZ = null
) {
    if (!barInfo || !barInfo.primaryLabel) return null;

    const camera = this.el.sceneEl.camera;
    if (!camera) return null;

    const cameraWorldPos = new THREE.Vector3();
    camera.getWorldPosition(cameraWorldPos);

    const cameraLocalPos = cameraWorldPos.clone();
    this.el.object3D.worldToLocal(cameraLocalPos);

    const localCameraY = cameraLocalPos.y;
    const zeroPlane    = this.zeroPlane || 0;
    const lastHeight   = typeof barInfo.lastHeight === 'number' ? barInfo.lastHeight : 0;
    const absHeight    = Math.max(0.001, Math.abs(lastHeight));
    const isPositiveBar = lastHeight >= 0;
    const offset       = barInfo.labelYOffset ?? this.labelYOffset ?? 0.03;

    let primaryLabelY;
    if (localCameraY >= zeroPlane) {
        primaryLabelY = (isPositiveBar ? zeroPlane + absHeight : zeroPlane) + offset;
    } else {
        primaryLabelY = (isPositiveBar ? zeroPlane : zeroPlane - absHeight) - offset;
    }

    let x, z;
    if (targetX !== null && targetZ !== null) {
        x = targetX;
        z = targetZ;
    } else {
        const rawPos = barInfo.primaryLabel.getAttribute('position');
        const currentPos = typeof rawPos === 'string'
            ? (() => {
                const [px, py, pz] = rawPos.split(/\s+/).map(parseFloat);
                return { x: px, y: py, z: pz };
            })()
            : (rawPos || { x: 0, y: primaryLabelY, z: 0 });
        x = currentPos.x;
        z = currentPos.z;
    }

    return { x, y: primaryLabelY, z };
};

window.StockCubeCore.calculateZPositions = function (maxRows, totalBars, mode, index = 0, rank = 0, gridSize = 1) {
    const isVRMode = AppState.isTrueVRMode;

    const DEPTH_FACTOR = (isVRMode ? 1.7 : 0.5) * this.containerHeight / 2;

    let zOffset, z;

    const depthWithSpacing = this.barDepth + this.spacing;

    const dataSource = this.data?.dataSource || '';
    const useOriginalLogic = dataSource === 'mmap'

    if (mode === 'sector') {
        const totalDepth = (maxRows - 1) * depthWithSpacing;
        if (!useOriginalLogic) {
            const maxRank = maxRows - 1;
            const invertedRank = maxRank - rank;
            z = (invertedRank * depthWithSpacing) - (totalDepth / 2);
        } else {
            z = (rank * depthWithSpacing) - (totalDepth / 2);
        }
    } else if (mode === 'grid') {
        const row = Math.floor(index / gridSize);
        let totalRows = Math.ceil(totalBars / gridSize);
        let totalDepth;
        if (this.data && ['sip', 'dividend'].includes(this.data.dataSource)) {
            totalDepth = (maxRows - 1) * depthWithSpacing;
        } else {
            totalDepth = (totalRows - 1) * depthWithSpacing;
        }
        if (!useOriginalLogic) {
            const maxRow = totalRows - 1;
            const invertedRow = maxRow - row;
            z = (invertedRow * depthWithSpacing) - (totalDepth / 2);
        } else {
            z = (row * depthWithSpacing) - (totalDepth / 2);
        }
    } else {
        throw new Error(`Unknown mode: ${mode}`);
    }
    zOffset = 0;

    return { zOffset, z };
};

window.StockCubeCore.calculateSectorLabelPosition = function (rotationData = null) {
    if (!this.sectorLabels || !this.sectorLabels.size) return;

    const sectorLabelStyle = this.platformConfig ? this.platformConfig.sectorLabelStyle : {};
    const yOffset = sectorLabelStyle.yOffset || 0;
    const margin = sectorLabelStyle.margin || 0.08;

    let halfDepth = (sectorLabelStyle.zOffset || 0.15);
    const zeroPlane = document.querySelector('#zero-plane');
    if (zeroPlane) {
        halfDepth = parseFloat(zeroPlane.getAttribute('height')) / 2;
    } else if (this.stockBars && this.stockBars.size > 0) {
        let maxZ = -Infinity, minZ = Infinity;
        this.stockBars.forEach((barInfo) => {
            if (typeof barInfo.z === 'number') {
                maxZ = Math.max(maxZ, barInfo.z);
                minZ = Math.min(minZ, barInfo.z);
            }
        });
        if (maxZ > -Infinity && minZ < Infinity) {
            halfDepth = (maxZ - minZ) / 2 + (this.barDepth || 0.02) / 2;
        }
    }

    let labelZ = halfDepth + margin;
    let isCameraFront = true;
    let textFaceTowardCamera = true;

    const sceneEl = this.el && this.el.sceneEl;
    const camera = sceneEl && sceneEl.camera;
    if (camera && this.el && this.el.object3D) {
        this.el.object3D.updateMatrixWorld(true);

        const cameraWorldPos = new THREE.Vector3();
        camera.getWorldPosition(cameraWorldPos);

        const chartWorldPos = new THREE.Vector3();
        this.el.object3D.getWorldPosition(chartWorldPos);

        const worldQuat = new THREE.Quaternion();
        this.el.object3D.getWorldQuaternion(worldQuat);

        const toCam = cameraWorldPos.clone().sub(chartWorldPos);
        const localZInWorld = new THREE.Vector3(0, 0, 1).applyQuaternion(worldQuat);
        const dotZ = toCam.dot(localZInWorld);
        isCameraFront = dotZ >= 0;
        labelZ = isCameraFront ? halfDepth + margin : -(halfDepth + margin);
        const localYInWorld = new THREE.Vector3(0, 1, 0).applyQuaternion(worldQuat);
        const dotY = toCam.dot(localYInWorld);
        textFaceTowardCamera = dotY >= 0;
    }
    const isFront = labelZ >= 0;

    // --- GLOBAL LABELS' PARAMS ---
    const localYOffset = sectorLabelStyle.localYOffset || 0.01;
    const localZOffset = sectorLabelStyle.localZOffset || 0.02;

    let rightZ = 1;
    if (camera && this.el && this.el.object3D) {
        const chartWorldQuat = new THREE.Quaternion();
        this.el.object3D.getWorldQuaternion(chartWorldQuat);
        
        const cameraQuat = new THREE.Quaternion();
        camera.getWorldQuaternion(cameraQuat);
        
        const cameraRightWorld = new THREE.Vector3(1, 0, 0).applyQuaternion(cameraQuat);
        const rightInParent = cameraRightWorld.applyQuaternion(chartWorldQuat.invert());
        rightZ = rightInParent.z >= 0 ? 1 : -1;
    }

    const inwardZ = isFront ? -1 : 1;
    const rotX = textFaceTowardCamera ? -90 : 90;
    const signX = textFaceTowardCamera ? -1 : 1;
    const growthDirZ = rightZ;
    const signZ = growthDirZ * signX;
    const finalRotZ = signZ * 90;
    
    const isAnchorLeft = (inwardZ === rightZ);
    const finalAnchor = isAnchorLeft ? 'left' : 'right';
    const finalZOffset = inwardZ * localZOffset;

    const textRotation = `${rotX} 0 ${finalRotZ}`;
    const textPosition = `0 ${localYOffset} ${finalZOffset}`;
    // --------------------------------------------------------

    this.sectorLabels.forEach((labelGroup) => {
        const x = typeof labelGroup.intendedX === 'number' ? labelGroup.intendedX : 0;
        const baseLabelY = typeof labelGroup.baseLabelY === 'number' ? labelGroup.baseLabelY : (this.zeroPlane - 0.05);
        const appliedY = this.platformConfig.platform === 'vr' ? yOffset : baseLabelY + yOffset;
        labelGroup.setAttribute('position', `${x} ${appliedY} ${labelZ}`);

        const sectorLabel = labelGroup.querySelector('a-troika-text');
        if (sectorLabel) {
            sectorLabel.setAttribute('rotation', textRotation);
            sectorLabel.setAttribute('position', textPosition);
            sectorLabel.setAttribute('anchor', finalAnchor);
        }
    });
};

window.StockCubeCore.createSectorLabel = function (container, sectorId, x, labelY, labelColor, avgHeightValue) {
    const sectorLabelStyle = this.platformConfig.sectorLabelStyle || {};
    const isLightTheme = document.body.classList.contains('light') || document.body.classList.contains('cb-light');
    const labelTextColor = isLightTheme ? '#111827' : '#FFFFFF';
    const labelOutlineColor = isLightTheme ? '#FFFFFF' : (sectorLabelStyle.outlineColor || '#000000');

    const labelGroup = document.createElement("a-entity");
    const zOffset = sectorLabelStyle.zOffset || 0.15;
    const yOffset = sectorLabelStyle.yOffset || 0;
    labelGroup.setAttribute("position", `${x} ${labelY + yOffset} ${zOffset}`);
    labelGroup.baseLabelY = labelY;
    labelGroup.intendedX = x;

    const sectorLabel = document.createElement("a-troika-text");
    sectorLabel.setAttribute("value", this.sectorNames[sectorId] || `Sector ${sectorId}`);
    sectorLabel.setAttribute("font-size", sectorLabelStyle.fontSize || this.fontSize);
    sectorLabel.setAttribute("align", "left");
    sectorLabel.setAttribute("anchor", "left");
    sectorLabel.setAttribute("baseline", "center");
    sectorLabel.setAttribute("color", labelTextColor);

    const localYOffset = sectorLabelStyle.localYOffset || 0.01;
    const localZOffset = sectorLabelStyle.localZOffset || 0.02;

    sectorLabel.setAttribute("rotation", "-90 0 90");
    sectorLabel.setAttribute("position", `0 ${localYOffset} ${-localZOffset}`);
    sectorLabel.setAttribute("depth-offset", sectorLabelStyle.depthOffset || "0");

    if (sectorLabelStyle.strokeWidth) {
        sectorLabel.setAttribute("stroke-width", sectorLabelStyle.strokeWidth);
        sectorLabel.setAttribute("stroke-color", isLightTheme ? "#FFFFFF" : (sectorLabelStyle.strokeColor || "#000000"));
        sectorLabel.setAttribute("stroke-opacity", sectorLabelStyle.strokeOpacity || "1.0");
        sectorLabel.setAttribute("outline-width", sectorLabelStyle.outlineWidth || "0%");
        sectorLabel.setAttribute("fill-opacity", sectorLabelStyle.fillOpacity || "1.0");
    } else if (sectorLabelStyle.outlineWidth) {
        sectorLabel.setAttribute("outline-width", sectorLabelStyle.outlineWidth);
        sectorLabel.setAttribute("outline-color", labelOutlineColor);
    }

    sectorLabel.setAttribute("transition", "0.5");

    const circle = document.createElement("a-circle");
    circle.setAttribute("radius", sectorLabelStyle.circleRadius || 0.012);
    circle.setAttribute("color", labelColor);
    circle.setAttribute("position", `0 ${localYOffset} 0`);
    circle.setAttribute("rotation", "-90 0 0");
    circle.setAttribute("material", "shader: flat; side: double");

    labelGroup.appendChild(sectorLabel);
    labelGroup.appendChild(circle);

    if (labelGroup.object3D) {
        labelGroup.object3D.renderOrder = 9999;
    } else {
        labelGroup.addEventListener('loaded', () => {
            try {
                if (labelGroup.object3D) labelGroup.object3D.renderOrder = 9999;
            } catch (e) {}
        }, { once: true });
    }

    const originalSetAttribute = labelGroup.setAttribute.bind(labelGroup);
    labelGroup.setAttribute = function(attr, val) {
        originalSetAttribute(attr, val);
        if (attr === 'value') {
            sectorLabel.setAttribute('value', val);
        } else if (attr === 'color') {
            circle.setAttribute('color', val);
        }
    };

    if (this.platformConfig.platform === 'desktop') {
        sectorLabel.setAttribute("class", "bloom-exclude");
        circle.setAttribute("class", "bloom-exclude");

        const setLayer1 = (obj) => {
            if (obj) {
                obj.layers.set(1);
                obj.children.forEach(setLayer1);
            }
        };

        if (sectorLabel.object3D) {
            setLayer1(sectorLabel.object3D);
        } else {
            sectorLabel.addEventListener('loaded', () => {
                if (sectorLabel.object3D) setLayer1(sectorLabel.object3D);
            }, { once: true });
        }
        sectorLabel.addEventListener('troika-text-ready', () => {
            if (sectorLabel.object3D) setLayer1(sectorLabel.object3D);
        });

        if (circle.object3D) {
            setLayer1(circle.object3D);
        } else {
            circle.addEventListener('loaded', () => {
                if (circle.object3D) setLayer1(circle.object3D);
            }, { once: true });
        }

        setTimeout(() => {
            if (sectorLabel.object3D) {
                sectorLabel.object3D.traverse((child) => {
                    if (child.material) {
                        const mats = Array.isArray(child.material) ? child.material : [child.material];
                        mats.forEach(mat => {
                            if (mat) {
                                if (mat.emissive) {
                                    mat.emissive.setHex(0x000000);
                                }
                                mat.userData = mat.userData || {};
                                mat.userData.bloom = false;
                            }
                        });
                    }
                });
            }
        }, 500);
    }

    container.appendChild(labelGroup);
    this.sectorLabels.set(sectorId, labelGroup);
};

